import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { ISavedQueries, IXSifterSavedQueryListRequest } from "../../types/XSifterQueries";
import XSifterQueryService from "../../services/XSifterQueryService";
import { Table, TableHead, TableRow, TableCell, TableBody, Pagination, Container, TextField, InputAdornment, FormControl, Box, InputLabel, Input, Grid, Button, Switch, FormControlLabel } from "@mui/material";
import SavedQueryRow from "./SavedQueryRow";
import SearchIcon from '@mui/icons-material/Search';
import SortableFieldHeader from "../common/SortableFieldHeader";
import { useAppDispatch } from "../../store/hooks";
import { setDisplayMessage, setDisplayMessageType } from "../../store/XSifterAppReducer";
import DeleteQueryConfirmation from "./DeleteQueryConfirmation";
import { pluralizeCount } from "../../types/FormattingHelper";
import { showApiError } from "../../services/ApiErrorHelper";

interface IXSifterSavedQueryListProps {
}

const XSifterSavedQueryList: React.FC<IXSifterSavedQueryListProps> = (props: IXSifterSavedQueryListProps) => {
    const [savedQueries, setSavedQueries] = useState<ISavedQueries>();
    const navigate = useNavigate();
    const queryService = new XSifterQueryService();
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [currentOffset, setCurrentOffset] = useState<number>(0);
    const [listRequest, setListRequest] = useState<IXSifterSavedQueryListRequest>({
        limit: 10,
        offset: 0,
        submitted: true
    });
    const [deleteQueryId, setDeleteQueryId] = useState<string | null>(null);
    const dispatch = useAppDispatch();    

    const itemsPerPage: number = 10;
    
    useEffect(() => {
        refreshSavedQueryList(listRequest);
    }, []);

    const refreshSavedQueryList = (request: IXSifterSavedQueryListRequest) => {
        queryService.getSavedQueries(request)
            .then((response) => {
                setupSavedQueryResponse(request, response);
            })
            .catch((error) => {

            });
    }

    const setupSavedQueryResponse = (request: IXSifterSavedQueryListRequest, response: ISavedQueries) => {
        let totalItems = response.meta.count;
        let itemsPerPage = response.meta.limit;
        let currentOffset = response.meta.offset;
        if (totalItems === 0) {
            setTotalPages(0);
            setPageNumber(0);
            setCurrentOffset(0);
        } else {
            let totalPages = Math.ceil(totalItems / itemsPerPage);
            let currentPage = Math.ceil(currentOffset / itemsPerPage) + 1;
            setTotalPages(totalPages);
            setPageNumber(currentPage);
            setCurrentOffset(currentOffset);
        }
        setListRequest({
            ...request,
            offset: response.meta.offset,
            limit: response.meta.limit
        });
        setSavedQueries(response);
    }

    const duplicateQuery = (queryId: string) => {
        navigate(`/app/query?queryId=${queryId}&duplicate=true`);
    }

    const handlePageChange =(event: React.ChangeEvent<unknown>, value: number) => {
        // TODO - change this hardcoded item
        let newOffset = (value-1) * 10; // 10 is hardcoded offset
        refreshSavedQueryList({
            ...listRequest,
            offset: newOffset
        });
    }

    const filterQueryList = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        refreshSavedQueryList({
            ...listRequest,
            query: event.target.value
        });
    }

    const filterSubmittedList = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        refreshSavedQueryList({
            ...listRequest,
            submitted: checked
        });
    }

    const sortQueryList = (field: string, direction: "a"|"d") => {
        refreshSavedQueryList({
            ...listRequest,
            sortField: field,
            sortDirection: direction
        })
    }

    const onDeleteQuery = (queryId: string) => {
        setDeleteQueryId(queryId);
    }

    const onQueryDeleted = (queryId: string) => {
        if (!queryId)
            return;

        queryService.deleteSavedQuery(queryId)
        .then((response) => {
            dispatch(setDisplayMessage(`Query '${response.title}' was deleted.`));
            dispatch(setDisplayMessageType("success"))
            setDeleteQueryId(null);
            refreshSavedQueryList(listRequest);
        }).catch((error) => {
            setDeleteQueryId(null);
            showApiError(dispatch, error, "Could not delete query");
        });
    }

    const onDeleteDialogClosed = () => {
        setDeleteQueryId(null);
    }

    return (
        <>
            <Grid container>
                <Grid size={9}>
                    <h1>Saved Queries</h1>
                </Grid>
                <Grid size={3} sx={{ textAlign: "right" }}>
                    <Button variant='outlined' onClick={() => { navigate("/app")}} sx={{ marginBottom: "16px;", marginTop: "16px"}}>
                        &laquo; back
                    </Button>
                </Grid>
            </Grid>
            <div>
                <Grid container component="div">
                <Grid size={{ xs: 4 }} component="div">
                    <Box sx={{flexGrow: 0 }}>
                        <FormControl variant="standard">
                            <InputLabel htmlFor={`search-query-input`}>
                                Filter queries
                            </InputLabel>
                            <Input
                                id={`search-query-input`}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                }
                                onChange={filterQueryList}
                            />
                        </FormControl>
                    </Box>
                </Grid>
                <Grid size={{ xs: 3 }} component="div">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={listRequest.submitted ?? true}
                                onChange={filterSubmittedList}
                            />
                        }
                        label="Submitted only"
                    />
                </Grid>
                <Grid size={{ xs: 4 }} style={{ textAlign: "right", alignContent: "flex-end"}} component="div">
                    <Pagination count={totalPages} page={pageNumber} onChange={handlePageChange} />
                </Grid>
                <Grid size={{ xs: 1 }} component="div" style={{ paddingTop: "20px"}}>
                    {pluralizeCount(savedQueries?.meta?.count, "result", "s")}
                </Grid>
                </Grid>
            </div>
            <Table className="x-table-banded">
                <TableHead>
                    <TableRow>
                        <SortableFieldHeader
                            currentDirection={listRequest.sortDirection}
                            currentField={listRequest.sortField}
                            sortField="timestamp"
                            onSort={sortQueryList}
                        >Timestamp</SortableFieldHeader>
                        <SortableFieldHeader
                            currentDirection={listRequest.sortDirection}
                            currentField={listRequest.sortField}
                            sortField="title"
                            onSort={sortQueryList}
                        >Title</SortableFieldHeader>
                        <SortableFieldHeader
                            currentDirection={listRequest.sortDirection}
                            currentField={listRequest.sortField}
                            sortField="estimated"
                            onSort={sortQueryList}
                        >Estimated Posts</SortableFieldHeader>
                        <SortableFieldHeader
                            currentDirection={listRequest.sortDirection}
                            currentField={listRequest.sortField}
                            sortField="downloaded"
                            onSort={sortQueryList}
                        >Downloaded Posts</SortableFieldHeader>
                        <TableCell>Re-use or Delete Query</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {savedQueries ? 
                        savedQueries.items.map(x => <SavedQueryRow 
                            key={x.savedQueryId} 
                            item={x} 
                            onDuplicateItem={duplicateQuery} 
                            onDeleteQuery={onDeleteQuery}
                        />)
                        : 
                        <TableRow>
                            <TableCell colSpan={5}><em>No saved queries to display</em></TableCell>
                        </TableRow>
                    }
                </TableBody>
            </Table>
            <DeleteQueryConfirmation queryId={deleteQueryId} onDialogClosed={onDeleteDialogClosed} onDeleteQuery={onQueryDeleted} />
        </>);
}

export default XSifterSavedQueryList;

